import { Component } from 'preact';
import { Link } from 'preact-router/match';

import {
	convertSpeedUnitToWindRunUnit,
	formatPressure,
	formatRainfall,
	formatRainRate,
	formatTemperature,
	formatTemperatureInterval,
	formatWindGust,
	formatWindSpeed,
	getDateTimeFormatConverter
} from '../../utils/formatters';
import { convertWindBearing16 } from '../../utils/convert-wind-bearing';
import style from './style.scss';

/**
 * The RecordsTable component displays a responsive table of weather records.
 *
 * props:
 * - data: records data set
 * - isMonthly: is this a monthly records set (will only contain a subset of the full records)
 */
class RecordsTable extends Component {

	constructor(props, context) {
		super(props, context);

		this.units = context.userSettings.units;

		this.formatters = {
			date: getDateTimeFormatConverter('%Y-%m-%d', '%-d %b %Y'),
			time: getDateTimeFormatConverter('%H:%M:%S', '%-I:%M %p'),
			temperature: t => formatTemperature(t, this.units.temperature) + '°' + this.units.temperature,
			temperatureInterval: t => formatTemperatureInterval(t, this.units.temperature) + '°' + this.units.temperature,
			rainfall: r => formatRainfall(r, this.units.rain) + ' ' + this.units.rain,
			rainRate: r => formatRainRate(r, this.units.rain) + ' ' + this.units.rain + '/hr',
			pressure: p => formatPressure(p, this.units.pressure) + ' ' + this.units.pressure,
			windGust: w => formatWindGust(w, this.units.speed) + ' ' + this.units.speed,
			windRun: w => formatWindSpeed(w, this.units.speed) + ' ' + convertSpeedUnitToWindRunUnit(this.units.speed)
		};

	}

	renderTable(data) {
		return (
			<div role="table" class={style.records_table}>

				<div role="rowgroup" class={style.column_headings}>
					<div role="row" class={style.row}>
						<div role="columnheader" class={style.label_cell}>Record</div>
						<div role="columnheader" class={style.value_cell}>Value</div>
						<div role="columnheader" class={style.datetime_cell}>Date/Time</div>
					</div>
				</div>

				<div role="rowgroup">

					{
						this.renderRow(
							'Highest temperature',
							this.formatters.temperature(data.highest_HighTemperature.highTemperature),
							data.highest_HighTemperature.date,
							data.highest_HighTemperature.highTemperatureTime
						)
					}

					{
						this.renderRow(
							'Lowest temperature',
							this.formatters.temperature(data.lowest_LowTemperature.lowTemperature),
							data.lowest_LowTemperature.date,
							data.lowest_LowTemperature.lowTemperatureTime
						)
					}

					{
						this.renderRow(
							'Lowest daily maximum',
							this.formatters.temperature(data.lowest_HighTemperature.highTemperature),
							data.lowest_HighTemperature.date,
							data.lowest_HighTemperature.highTemperatureTime
						)
					}

					{
						this.renderRow(
							'Highest daily minimum',
							this.formatters.temperature(data.highest_LowTemperature.lowTemperature),
							data.highest_LowTemperature.date,
							data.highest_LowTemperature.lowTemperatureTime
						)
					}

					{
						this.renderRow(
							'Overall warmest day',
							this.formatters.temperature(data.highest_MeanTemperature.avgTemperature),
							data.highest_MeanTemperature.date,
							null
						)
					}

					{
						this.renderRow(
							'Overall coolest day',
							this.formatters.temperature(data.lowest_MeanTemperature.avgTemperature),
							data.lowest_MeanTemperature.date,
							null
						)
					}

					{
						this.renderRow(
							'Largest temperature range',
							this.formatters.temperatureInterval(data.largest_TemperatureRange.temperatureRange),
							data.largest_TemperatureRange.date,
							null
						)
					}

					{
						this.renderRow(
							'Smallest temperature range',
							this.formatters.temperatureInterval(data.smallest_TemperatureRange.temperatureRange),
							data.smallest_TemperatureRange.date,
							null
						)
					}

					{
						this.renderRow(
							'Highest daily rainfall',
							this.formatters.rainfall(data.highest_Rainfall.rainfall),
							data.highest_Rainfall.date,
							null
						)
					}

					{
						this.renderRow(
							'Highest 1 hour rainfall',
							this.formatters.rainfall(data.highest_HighHourlyRain.highHourlyRain),
							data.highest_HighHourlyRain.date,
							data.highest_HighHourlyRain.highHourlyRainTime
						)
					}

					{
						this.renderRow(
							'Highest rain rate',
							this.formatters.rainRate(data.highest_HighRainRate.highRainRate),
							data.highest_HighRainRate.date,
							data.highest_HighRainRate.highRainRateTime
						)
					}

					{
						this.renderRow(
							'Highest wind gust',
							convertWindBearing16(data.highest_HighWindGust.highWindGustBearing) + ' ' + this.formatters.windGust(data.highest_HighWindGust.highWindGust),
							data.highest_HighWindGust.date,
							data.highest_HighWindGust.highWindGustTime
						)
					}

					{
						this.renderRow(
							'Highest wind run',
							this.formatters.windRun(data.highest_WindRun.windRun),
							data.highest_WindRun.date,
							null
						)
					}
					{
						this.renderRow(
							'Highest pressure',
							this.formatters.pressure(data.highest_HighPressure.highPressure),
							data.highest_HighPressure.date,
							data.highest_HighPressure.highPressureTime
						)
					}

					{
						this.renderRow(
							'Lowest pressure',
							this.formatters.pressure(data.lowest_LowPressure.lowPressure),
							data.lowest_LowPressure.date,
							data.lowest_LowPressure.lowPressureTime
						)
					}
					{
						this.renderRow(
							'Highest dewpoint',
							this.formatters.temperature(data.highest_HighDewpoint.highDewpoint),
							data.highest_HighDewpoint.date,
							data.highest_HighDewpoint.highDewpointTime
						)
					}

					{
						this.renderRow(
							'Lowest dewpoint',
							this.formatters.temperature(data.lowest_LowDewpoint.lowDewpoint),
							data.lowest_LowDewpoint.date,
							data.lowest_LowDewpoint.lowDewpointTime
						)
					}

					{
						this.renderRow(
							'Lowest humidity',
							data.lowest_LowHumidity.lowHumidity + '%',
							data.lowest_LowHumidity.date,
							data.lowest_LowHumidity.lowHumidityTime
						)
					}

					{
						this.props.isMonthly ? null : this.renderExtraRecords(data)
					}

				</div>

			</div>
		);
	}

	renderExtraRecords(data) {
		return [
			this.renderRow(
				'Highest 7 day rainfall',
				this.formatters.rainfall(data.highest_Rainfall7days.rainfall),
				[data.highest_Rainfall7days.startDate, data.highest_Rainfall7days.endDate],
				null
			),
			this.renderRow(
				'Highest 30 day rainfall',
				this.formatters.rainfall(data.highest_Rainfall30days.rainfall),
				[data.highest_Rainfall30days.startDate, data.highest_Rainfall30days.endDate],
				null
			),
			this.renderRow(
				'Highest 60 day rainfall',
				this.formatters.rainfall(data.highest_Rainfall60days.rainfall),
				[data.highest_Rainfall60days.startDate, data.highest_Rainfall60days.endDate],
				null
			),
			this.renderRow(
				'Highest 90 day rainfall',
				this.formatters.rainfall(data.highest_Rainfall90days.rainfall),
				[data.highest_Rainfall90days.startDate, data.highest_Rainfall90days.endDate],
				null
			),
			this.renderRow(
				'Highest 365 day rainfall',
				this.formatters.rainfall(data.highest_Rainfall365days.rainfall),
				[data.highest_Rainfall365days.startDate, data.highest_Rainfall365days.endDate],
				null
			),
			this.renderRow(
				'Lowest 30 day rainfall',
				this.formatters.rainfall(data.lowest_Rainfall30days.rainfall),
				[data.lowest_Rainfall30days.startDate, data.lowest_Rainfall30days.endDate],
				null
			),
			this.renderRow(
				'Lowest 60 day rainfall',
				this.formatters.rainfall(data.lowest_Rainfall60days.rainfall),
				[data.lowest_Rainfall60days.startDate, data.lowest_Rainfall60days.endDate],
				null
			),
			this.renderRow(
				'Lowest 90 day rainfall',
				this.formatters.rainfall(data.lowest_Rainfall90days.rainfall),
				[data.lowest_Rainfall90days.startDate, data.lowest_Rainfall90days.endDate],
				null
			),
			this.renderRow(
				'Lowest 365 day rainfall',
				this.formatters.rainfall(data.lowest_Rainfall365days.rainfall),
				[data.lowest_Rainfall365days.startDate, data.lowest_Rainfall365days.endDate],
				null
			),
			this.renderRow(
				<span>Most consecutive wet days (&ge;1 mm)</span>,
				data.longest_PeriodWetDays.days,
				[data.longest_PeriodWetDays.startDate, data.longest_PeriodWetDays.endDate],
				null
			),
			this.renderRow(
				<span>Most consecutive non-wet days (&lt;1 mm)</span>,
				data.longest_PeriodNoWetDays.days,
				[data.longest_PeriodNoWetDays.startDate, data.longest_PeriodNoWetDays.endDate],
				null
			),
			this.renderRow(
				'Most consecutive days with 0.0mm',
				data.longest_PeriodNoRainfall.days,
				[data.longest_PeriodNoRainfall.startDate, data.longest_PeriodNoRainfall.endDate],
				null
			)
		];
	}

	renderRow(label, value, date, time) {
		const dates = Array.isArray(date) ? date : [date];

		return (
			<div role="row" class={style.row}>
				<div role="rowheader" class={style.label_cell}>{ label }</div>
				<div role="cell" class={style.value_cell}>{ value }</div>
				<div role="cell" class={style.datetime_cell}>
					<div class={style.date}>
						{
							dates.map((d, i) => {
								return [
									i > 0 ? ' to' : null,
									i > 0 ? <br /> : null,
									<Link href={`/history-daily/${d}`}>{ this.formatters.date(d) }</Link>
								];
							})
						}
					</div>
					<div class={style.time}>{ time ? this.formatters.time(time) : null }</div>
				</div>
			</div>
		);
	}

	render(props) {
		return (
			<div class={style.records_table}>
				{ this.renderTable(props.data) }
			</div>
		);
	}

}

export default RecordsTable;



// WEBPACK FOOTER //
// ./components/records-table/index.js